import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Store } from 'lucide-react';
import { PERMISSIONS, type BranchSummary } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { BooleanStatusBadge } from '@/components/ui/StatusBadge';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useBranchesQuery } from '../api/branches.api';

/**
 * Branch administration against the real API.
 *
 * Also the shell's end-to-end smoke test: token → tenant context → tenant-scoped query → response
 * envelope. If something in the auth pipeline breaks, this page shows it first.
 */
export function BranchesPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [search, setSearch] = useState('');

  // Keeps typing responsive without a debounce timer; the query re-runs on the settled value.
  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useBranchesQuery({
    search: deferredSearch,
  });

  const columns: Column<BranchSummary>[] = [
    {
      key: 'name',
      header: t('branches.name'),
      primary: true,
      render: (branch) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium">{branch.name}</span>
          <span className="text-muted font-mono text-xs">{branch.code}</span>
        </span>
      ),
    },
    {
      key: 'phone',
      header: t('branches.phone'),
      render: (branch) =>
        branch.phone ? (
          // Phone numbers are LTR even in an Arabic layout.
          <span dir="ltr" className="text-muted">
            {branch.phone}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'address',
      header: t('branches.address'),
      render: (branch) => <span className="text-muted">{branch.address ?? '—'}</span>,
    },
    {
      key: 'timezone',
      header: t('branches.timezone'),
      render: (branch) => (
        <span dir="ltr" className="text-muted text-xs">
          {branch.timezone}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('branches.status'),
      render: (branch) => <BooleanStatusBadge isActive={branch.isActive} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      hideOnCard: true,
      render: () => (
        // Editing arrives with the branch management module; the column reserves its place.
        <Button variant="ghost" size="sm" disabled title={t('common.comingSoon')}>
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('branches.title')}
        subtitle={t('branches.subtitle')}
        actions={
          can(PERMISSIONS.BRANCHES_CREATE) ? (
            <Button disabled title={t('common.comingSoon')}>
              {t('branches.create')}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader
          title={t('branches.title')}
          subtitle={data ? t('branches.count', { count: data.pagination.totalItems }) : undefined}
          action={
            <div className="w-full max-w-56">
              <Input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('common.search')}
                aria-label={t('branches.searchLabel')}
              />
            </div>
          }
        />

        <DataTable
          caption={t('branches.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(branch) => branch.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('branches.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Store}
          emptyTitle={search ? t('branches.noMatches') : t('branches.empty')}
          emptyDescription={search ? t('branches.noMatchesHint') : t('branches.emptyHint')}
        />

        {isFetching && !isPending ? (
          <p className="text-muted border-line border-t px-5 py-2 text-xs">
            {t('common.refreshing')}
          </p>
        ) : null}
      </Card>
    </div>
  );
}
